import { Component } from 'react'

/**
 * Cattura gli errori di render dei figli e mostra un messaggio invece di
 * lasciare la pagina bianca (React smonta l'albero su un throw non catturato).
 * In dev mostra anche il dettaglio dell'errore per il debug.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { error: null, stack: null }
  }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidCatch(error, info) {
    // Log utile in console + ricavabile per Sentry in futuro.
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary ha catturato un errore:', error, info)
    // Tengo anche la catena di componenti: il dettaglio in pagina (sotto)
    // dice DOVE è saltato, utile quando si usa da telefono (console non
    // raggiungibile) e l'utente deve segnalarlo.
    this.setState({ stack: info?.componentStack || null })

    // Chunk JS mancante dopo un deploy (l'app in memoria punta a file
    // della build precedente): un reload scarica la versione nuova.
    // Il flag in sessionStorage evita loop se il reload non risolve.
    const msg = String(error?.message || error)
    const isStaleChunk =
      /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError|error loading dynamically imported module/i.test(
        msg,
      )
    if (isStaleChunk && !sessionStorage.getItem('chunk-reload')) {
      sessionStorage.setItem('chunk-reload', '1')
      window.location.reload()
    } else if (!isStaleChunk) {
      sessionStorage.removeItem('chunk-reload')
    }
  }

  handleReset = () => {
    this.setState({ error: null })
  }

  render() {
    if (this.state.error) {
      // dettaglio compatto (messaggio + pagina + primo componente che ha
      // saltato) più, quando c'è, lo stack completo: mostrato anche in
      // produzione, così chi lo incontra — spesso da telefono, dove la
      // console non è raggiungibile — può copiarlo e segnalarlo.
      const dove = this.state.stack
        ? this.state.stack.trim().split('\n')[0].trim()
        : ''
      const dettaglio = [
        String(this.state.error?.message || this.state.error),
        `Pagina: ${window.location.pathname}`,
        dove && `Componente: ${dove}`,
        this.state.error?.stack ? `\n${this.state.error.stack}` : '',
      ]
        .filter(Boolean)
        .join('\n')
      return (
        <div className="p-8 max-w-2xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-red-800 mb-2">
              Si è verificato un errore in questa pagina
            </h2>
            <p className="text-sm text-red-700 mb-4">
              Ricarica la pagina o torna indietro. Se il problema persiste,
              copia i dettagli qui sotto e mandali all&apos;amministratore.
            </p>
            <details className="mb-1">
              <summary className="text-sm text-red-700 cursor-pointer select-none">
                Dettagli tecnici
              </summary>
              <pre className="mt-2 text-xs bg-white border border-red-200 rounded p-3 overflow-auto text-red-900 whitespace-pre-wrap">
                {dettaglio}
              </pre>
            </details>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-4 bg-red-600 text-white px-4 py-2 rounded-md text-sm hover:bg-red-700"
            >
              Ricarica
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
